import React, { useEffect, useState } from 'react';
import ShortSaleStatistics from '../ShortSaleStatistics';
import { getChartsComponent } from '../salesCharts/helpers';
import { useSelector } from 'react-redux';
import useSalesRequest from '../../../../hooks/useSaleRequest';
import parseShortSummary from '../parsers/parseShortSummary';
import parseDailySales from '../parsers/parseDailySales';
import SalesStatisticsTable from '../SalesStatisticsTable';
import parsePopularProducts from '../parsers/parsePopularProducts';
import parseEmployeesSales from '../parsers/parseEmployeesSales';

const SalesDataContainer = () => {
  const { getSalesData } = useSalesRequest();
  const filters = useSelector(state => state.saleFiltersSlice);
  const {
    periodStart,
    periodEnd,
    statisticType,
    employee,
    category,
    brand,
    productSkuBarcode,
    warehouse
  } = filters;

  const [loading, setLoading] = useState(false);

  const [currencyState, setCurrencyState] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  const [tableRowsRaw, setTableRowsRaw] = useState([]);
  const [summary, setSummary] = useState(null);
  const [mainColumns, setMainColumns] = useState([]);

  useEffect(() => {
    if (!periodStart || !periodEnd) {
      setChartData(null);
      setTableRows([]);
      setSummary(null);
      return;
    }

    setLoading(true);

    (async () => {
      const payload = {
        start_date: periodStart,
        end_date: periodEnd,
        statisticType: statisticType,
        employee: employee,
        category: category,
        brand: brand,
        product: productSkuBarcode,
        sku: productSkuBarcode,
        barcode: productSkuBarcode,
        warehouse: warehouse,
      };
      try {
        const result = await getSalesData(payload);

        if (!result ) {
          setChartData(null);
          setTableRows([]);
          setSummary(null);
          setMainColumns([]);

          return;
        }
        const aggregations = result.aggregations;
        const currency = result.branchInfo.currency;

        setCurrencyState(currency)

        const shortSummary = parseShortSummary(aggregations, {
          startDate: payload.start_date,
          endDate: payload.end_date,
          currency: currency
        });
        setSummary(shortSummary);

        let parsed = null;
        if (payload.statisticType === 'DAILY_SALES') {
          parsed = parseDailySales(aggregations, {
            startDate: payload.start_date,
            endDate: payload.end_date,
            currency
          });
        } else if (payload.statisticType === 'POPULAR_PRODUCTS') {
          parsed = parsePopularProducts(aggregations, { topN: 15, currency });
        } else if (payload.statisticType === 'EMPLOYEES_SALES') {
          parsed = parseEmployeesSales(aggregations, { topN: 15, currency });
        } else {
          parsed = parseDailySales(aggregations, {
            startDate: payload.start_date,
            endDate: payload.end_date,
            currency
          });
        }

        setChartData(parsed.charts || null);
        setTableRows(parsed.tableRows || []);
        setMainColumns(parsed.mainColumns || []);
        setTableRowsRaw(parsed.tableRowsRaw || []);

      } catch (err) {
        if (err && (err.name === 'AbortError' || err.name === 'CanceledError' || err.message === 'canceled')) {
          return;
        }
        console.error('SalesDataContainer getSalesData error', err);
      } finally {
        setLoading(false);
      }
    })();

  }, [
    periodStart,
    periodEnd,
    statisticType,
    employee,
    category,
    brand,
    productSkuBarcode,
    warehouse
  ]);

  const ChartsComponent = getChartsComponent(statisticType);
  return (
    <>
      <ShortSaleStatistics
        summary={summary}
        mainColumns={mainColumns}
        tableRowsRaw={tableRowsRaw}
        tableRows={tableRows}
        currency={currencyState}
      />
      <ChartsComponent data={chartData} currency={currencyState} />
      <SalesStatisticsTable
        statisticType={statisticType}
        tableRows={tableRows}
        tableRowsRaw={tableRowsRaw}
        mainColumns={mainColumns}
        currency={currencyState}
        rowsPerPage={5}
      />
    </>
  );
};

export default SalesDataContainer;